/**
 * useFetchLayers — viewport-gated layer fetcher dispatcher.
 *
 * On each map idle/move it decides which active layers need a refetch
 * for the current bbox/zoom, then dynamically imports the matching
 * country+api module (lazy memoised) and runs its fetcher. This keeps
 * the initial bundle tiny — ~20 country modules load on demand.
 *
 * Consumers: App.tsx calls fetchLayers() from map event handlers; the
 * fetcher modules live under src/api/{country}/.
 */
import React, { useCallback, useRef } from "react";
import type { MutableRefObject } from "react";
import type GeoJSON from "geojson";

// Types
import type {
  ActiveTab, CategoryGroup, BBox, Lang,
  HeritageSite, FolkloreRecord, ProtectedBuilding,
  Shipwreck, RockArt, IndustrialSite, BarrowSite, TownscapeSite,
} from "../types/index.ts";

// Constants
import {
  GROUP_TABS, SCAND_BOUNDS, DK_BOUNDS, NO_BOUNDS, SE_BOUNDS,
  FI_BOUNDS, IS_BOUNDS, FO_BOUNDS, GL_BOUNDS, GB_BOUNDS, EN_BOUNDS, SC_BOUNDS, IE_BOUNDS, IE_WRECK_BOUNDS, NI_BOUNDS, EE_BOUNDS, LV_BOUNDS, LT_BOUNDS,
  NL_BOUNDS,
  DE_BOUNDS,
} from "../constants/index.ts";

// i18n
import { t } from "../i18n/index.ts";

// Utils
import { bboxOverlaps, bboxOverlapsAny, isLikelyInland, viewportFitsACountryFile } from "../utils/geo.ts";
import { toHeritageGeoJSON, toSimpleGeoJSON } from "../utils/geojson.ts";
import { withBuildingYears } from "../api/buildingYears.ts";
import { isFolkloreDuplicate } from "../utils/folkloreDedup.ts";

// API — folklore (cache reading helpers stay static — they read in-memory
// state populated by App.tsx's lazy folklore loaders).
import { fetchISoFLegends, getDanishFolkloreCache, getSagaCache, getSagnagrunnurCache, getFinnishFolkloreCache } from "../api/folklore.ts";

// Heritage, buildings, shipwrecks, rockart, industrial and barrow modules
// are dynamic-imported below. The first viewport change after page load
// triggers a ~3-7 kB gzip chunk download per layer; subsequent pans use
// the cached module promise. The user already waits 400 ms of debounce
// before any fetch fires, so the import overhead is hidden inside the
// debounce window.

// ---------------------------------------------------------------------------
// Per-country lazy loaders — only import the module when the viewport
// actually overlaps that country bbox. Each loader memoizes the import
// promise so concurrent calls during a single pan share one network round.
// ---------------------------------------------------------------------------
let _finlandMod: Promise<typeof import("../api/finland.ts")> | null = null;
const loadFinland = () => (_finlandMod ??= import("../api/finland.ts"));
let _estoniaMod: Promise<typeof import("../api/estonia.ts")> | null = null;
const loadEstonia = () => (_estoniaMod ??= import("../api/estonia.ts"));
let _latviaMod: Promise<typeof import("../api/latvia.ts")> | null = null;
const loadLatvia = () => (_latviaMod ??= import("../api/latvia.ts"));
let _lithuaniaMod: Promise<typeof import("../api/lithuania.ts")> | null = null;
const loadLithuania = () => (_lithuaniaMod ??= import("../api/lithuania.ts"));
let _netherlandsMod: Promise<typeof import("../api/netherlands.ts")> | null = null;
const loadNetherlands = () => (_netherlandsMod ??= import("../api/netherlands.ts"));
let _germanyMod: Promise<typeof import("../api/germany.ts")> | null = null;
const loadGermany = () => (_germanyMod ??= import("../api/germany.ts"));
let _icelandMod: Promise<typeof import("../api/iceland.ts")> | null = null;
const loadIceland = () => (_icelandMod ??= import("../api/iceland.ts"));
let _faroeMod: Promise<typeof import("../api/faroe.ts")> | null = null;
const loadFaroe = () => (_faroeMod ??= import("../api/faroe.ts"));
let _greenlandMod: Promise<typeof import("../api/greenland.ts")> | null = null;
const loadGreenland = () => (_greenlandMod ??= import("../api/greenland.ts"));
let _englandMod: Promise<typeof import("../api/england.ts")> | null = null;
const loadEngland = () => (_englandMod ??= import("../api/england.ts"));
let _irelandMod: Promise<typeof import("../api/ireland.ts")> | null = null;
const loadIreland = () => (_irelandMod ??= import("../api/ireland.ts"));
let _walesMod: Promise<typeof import("../api/wales.ts")> | null = null;
const loadWales = () => (_walesMod ??= import("../api/wales.ts"));
let _scotlandMod: Promise<typeof import("../api/scotland.ts")> | null = null;
const loadScotland = () => (_scotlandMod ??= import("../api/scotland.ts"));
let _niMod: Promise<typeof import("../api/northernireland.ts")> | null = null;
const loadNI = () => (_niMod ??= import("../api/northernireland.ts"));
let _townscapeMod: Promise<typeof import("../api/townscape.ts")> | null = null;
const loadTownscape = () => (_townscapeMod ??= import("../api/townscape.ts"));
let _heritageMod: Promise<typeof import("../api/heritage.ts")> | null = null;
const loadHeritage = () => (_heritageMod ??= import("../api/heritage.ts"));
let _buildingsMod: Promise<typeof import("../api/buildings.ts")> | null = null;
const loadBuildings = () => (_buildingsMod ??= import("../api/buildings.ts"));
let _shipwrecksMod: Promise<typeof import("../api/shipwrecks.ts")> | null = null;
const loadShipwrecks = () => (_shipwrecksMod ??= import("../api/shipwrecks.ts"));
let _rockartMod: Promise<typeof import("../api/rockart.ts")> | null = null;
const loadRockart = () => (_rockartMod ??= import("../api/rockart.ts"));
let _industrialMod: Promise<typeof import("../api/industrial.ts")> | null = null;
const loadIndustrial = () => (_industrialMod ??= import("../api/industrial.ts"));
let _barrowMod: Promise<typeof import("../api/barrow.ts")> | null = null;
const loadBarrow = () => (_barrowMod ??= import("../api/barrow.ts"));

// ---------------------------------------------------------------------------
// Hook params
// ---------------------------------------------------------------------------

export interface UseFetchLayersParams {
  // Debounce timer refs
  debounceRef: MutableRefObject<ReturnType<typeof setTimeout> | undefined>;
  folkDebounceRef: MutableRefObject<ReturnType<typeof setTimeout> | undefined>;
  bldDebounceRef: MutableRefObject<ReturnType<typeof setTimeout> | undefined>;
  wreckDebounceRef: MutableRefObject<ReturnType<typeof setTimeout> | undefined>;
  rockDebounceRef: MutableRefObject<ReturnType<typeof setTimeout> | undefined>;
  indDebounceRef: MutableRefObject<ReturnType<typeof setTimeout> | undefined>;
  barDebounceRef: MutableRefObject<ReturnType<typeof setTimeout> | undefined>;
  enTownDebounceRef: MutableRefObject<ReturnType<typeof setTimeout> | undefined>;

  // Last-bbox refs (dedup)
  lastBboxRef: MutableRefObject<BBox | null>;
  folkLastBboxRef: MutableRefObject<BBox | null>;
  bldLastBboxRef: MutableRefObject<BBox | null>;
  wreckLastBboxRef: MutableRefObject<BBox | null>;
  rockLastBboxRef: MutableRefObject<BBox | null>;
  indLastBboxRef: MutableRefObject<BBox | null>;
  barLastBboxRef: MutableRefObject<BBox | null>;
  enTownLastBboxRef: MutableRefObject<BBox | null>;

  // Index refs
  folkloreIndex: MutableRefObject<Map<string, FolkloreRecord>>;
  folkloreByCoord: MutableRefObject<Map<string, FolkloreRecord[]>>;
  buildingIndex: MutableRefObject<Map<string, ProtectedBuilding>>;
  shipwreckIndex: MutableRefObject<Map<string, Shipwreck>>;
  rockArtIndex: MutableRefObject<Map<string, RockArt>>;
  industrialIndex: MutableRefObject<Map<string, IndustrialSite>>;
  barrowIndex: MutableRefObject<Map<string, BarrowSite>>;
  townscapeIndex: MutableRefObject<Map<string, TownscapeSite>>;

  // Static data refs
  wikidataArchRef: MutableRefObject<HeritageSite[]>;
  doggerHeritageCacheRef: MutableRefObject<HeritageSite[]>;
  romanCacheRef: MutableRefObject<HeritageSite[]>;
  hillfortCacheRef: MutableRefObject<HeritageSite[]>;
  hoardCacheRef: MutableRefObject<HeritageSite[]>;
  wdWreckCacheRef: MutableRefObject<Shipwreck[]>;

  // Deduplication
  heritageCoordSet: Set<string>;

  // Feature setters
  setHeritageFeatures: React.Dispatch<React.SetStateAction<GeoJSON.Feature[]>>;
  setFolkloreFeatures: React.Dispatch<React.SetStateAction<GeoJSON.Feature[]>>;
  setBuildingFeatures: React.Dispatch<React.SetStateAction<GeoJSON.Feature[]>>;
  setShipwreckFeatures: React.Dispatch<React.SetStateAction<GeoJSON.Feature[]>>;
  setRockArtFeatures: React.Dispatch<React.SetStateAction<GeoJSON.Feature[]>>;
  setIndustrialFeatures: React.Dispatch<React.SetStateAction<GeoJSON.Feature[]>>;
  setBarrowFeatures: React.Dispatch<React.SetStateAction<GeoJSON.Feature[]>>;
  setTownscapeFeatures: React.Dispatch<React.SetStateAction<GeoJSON.Feature[]>>;

  // Count setters
  setCounts: React.Dispatch<React.SetStateAction<{ dk: number; no: number; se: number }>>;
  setFolkloreCounts: React.Dispatch<React.SetStateAction<number>>;
  setBuildingCount: React.Dispatch<React.SetStateAction<number>>;
  setShipwreckCount: React.Dispatch<React.SetStateAction<number>>;
  setRockArtCount: React.Dispatch<React.SetStateAction<number>>;
  setIndustrialCount: React.Dispatch<React.SetStateAction<number>>;
  setBarrowCount: React.Dispatch<React.SetStateAction<number>>;
  setTownscapeCount: React.Dispatch<React.SetStateAction<number>>;

  // Loading / error state
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  addLoading: (tab: ActiveTab) => void;
  removeLoading: (tab: ActiveTab) => void;

  // Language (captured at hook-call time, matching original stale-closure behaviour)
  lang: Lang;
  /**
   * Whether the reader is on the default view of everything, everywhere.
   *
   * The file and proxy gates below are for that view only. A reader who has
   * picked a layer or a group has asked for it and gets it at any zoom, which
   * is what an `/explore/<category>` landing page is. A ref rather than a prop,
   * so a tab change does not rebuild every fetch callback.
   */
  browsingEverythingRef: MutableRefObject<boolean>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useFetchLayers(params: UseFetchLayersParams) {
  const {
    debounceRef, folkDebounceRef, bldDebounceRef, wreckDebounceRef,
    rockDebounceRef, indDebounceRef, barDebounceRef, enTownDebounceRef,
    lastBboxRef, folkLastBboxRef, bldLastBboxRef, wreckLastBboxRef,
    rockLastBboxRef, indLastBboxRef, barLastBboxRef, enTownLastBboxRef,
    folkloreIndex, folkloreByCoord,
    buildingIndex, shipwreckIndex, rockArtIndex, industrialIndex, barrowIndex, townscapeIndex,
    wikidataArchRef, doggerHeritageCacheRef, romanCacheRef, hillfortCacheRef, hoardCacheRef, wdWreckCacheRef,
    heritageCoordSet,
    setHeritageFeatures, setFolkloreFeatures, setBuildingFeatures,
    setShipwreckFeatures, setRockArtFeatures, setIndustrialFeatures,
    setBarrowFeatures, setTownscapeFeatures,
    setCounts, setFolkloreCounts, setBuildingCount,
    setShipwreckCount, setRockArtCount, setIndustrialCount,
    setBarrowCount, setTownscapeCount,
    setLoading, setError,
    addLoading, removeLoading,
    lang,
    browsingEverythingRef,
  } = params;

  // Accumulative heritage index — once a site is fetched it stays visible
  const heritageAccumRef = useRef<Map<string, HeritageSite>>(new Map());

  // `lang` is captured via a ref so language switches mid-session are
  // reflected in error-message translations without forcing every
  // fetchXSites useCallback to recreate (which would cascade into
  // fetchForLayers and re-trigger every consumer's useEffect deps).
  const langRef = useRef(lang);
  langRef.current = lang;

  /** Put the static heritage sets into the accumulator; true if any was new. */
  const addStaticHeritage = useCallback((): boolean => {
    let added = false;
    for (const cache of [wikidataArchRef, doggerHeritageCacheRef, romanCacheRef, hillfortCacheRef, hoardCacheRef]) {
      for (const s of cache.current) {
        if (!heritageAccumRef.current.has(s.id)) { heritageAccumRef.current.set(s.id, s); added = true; }
      }
    }
    return added;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** Draw the accumulated heritage index. */
  const publishHeritage = useCallback(() => {
    const all = Array.from(heritageAccumRef.current.values());
    let dk = 0, no = 0, se = 0;
    all.forEach(s => { if (s.country === "DK") dk++; else if (s.country === "NO") no++; else se++; });
    setHeritageFeatures(toHeritageGeoJSON(all).features);
    setCounts({ dk, no, se });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * For a static set that finished loading after the heritage fetch did.
   *
   * The sets were merged only at the end of a heritage fetch, so a file that
   * arrived later waited for the next one, and the same-bbox guard means that
   * is the reader's next pan. Over Orkney the Scottish register answered
   * first in about one visit in sixteen and the hillforts stayed off the map
   * (the timeline e2e was the witness, and it failed on main too).
   */
  const mergeStaticHeritage = useCallback(() => {
    if (addStaticHeritage()) publishHeritage();
  }, [addStaticHeritage, publishHeritage]);

  // --- HERITAGE FETCH ---
  const fetchHeritageSites = useCallback((bbox: BBox) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const last = lastBboxRef.current;
      if (last && Math.abs(bbox.minLng - last.minLng) < 0.01 && Math.abs(bbox.minLat - last.minLat) < 0.01 && Math.abs(bbox.maxLng - last.maxLng) < 0.01 && Math.abs(bbox.maxLat - last.maxLat) < 0.01) return;
      lastBboxRef.current = bbox;
      setLoading(true);
      setError(null);
      try {
        // A country-sized file is worth fetching for a view of a place and not
        // for a view of a continent; utils/geo.ts::viewportFitsACountryFile has
        // the measurement. Live per-bbox fetchers below are not gated on it.
        // Norway is the one live register behind this gate. The others answer a
        // continental bbox in kilobytes; Geonorge answers through our own edge
        // proxy, pages 2 000 records per layer, and cost 900 kB on a zoom-5 view
        // of Scandinavia even after the prose left the list answer (14 Sep 2026).
        const local = !browsingEverythingRef.current || viewportFitsACountryFile(bbox);
        const promises: Promise<HeritageSite[]>[] = [];
        if (bboxOverlaps(bbox, DK_BOUNDS)) promises.push(loadHeritage().then(m => m.fetchDanishSites(bbox)));
        if (bboxOverlaps(bbox, NO_BOUNDS) && local) promises.push(loadHeritage().then(m => m.fetchNorwegianSites(bbox)));
        // Sweden is read live from K-samsök in RDF, and over the default view of
        // everything that was 23 requests and 5,2 MB on a first visit, most of
        // the page's weight (measured on a phone 26 Sep 2026). Gated as
        // Norway is; a reader who picks the layer still gets it at any zoom.
        if (bboxOverlaps(bbox, SE_BOUNDS) && local) promises.push(loadHeritage().then(m => m.fetchSwedishSites(bbox)));
        if (bboxOverlaps(bbox, FI_BOUNDS)) promises.push(loadFinland().then(m => m.fetchFinnishHeritage(bbox)));
        if (bboxOverlaps(bbox, EE_BOUNDS)) {
          promises.push(loadEstonia().then(m => m.fetchEstonianHeritage(bbox)));
          promises.push(loadEstonia().then(m => m.fetchEstonianGreenAreas(bbox)));
        }
        if (bboxOverlaps(bbox, LV_BOUNDS) && local) promises.push(loadLatvia().then(m => m.fetchLatvianHeritage(bbox)));
        if (bboxOverlaps(bbox, LT_BOUNDS)) promises.push(loadLithuania().then(m => m.fetchLithuanianHeritage(bbox)));
        if (bboxOverlaps(bbox, NL_BOUNDS)) promises.push(loadNetherlands().then(m => m.fetchDutchHeritage(bbox)));
        if (bboxOverlaps(bbox, DE_BOUNDS)) promises.push(loadGermany().then(m => m.fetchGermanHeritage(bbox)));
        if (bboxOverlaps(bbox, IS_BOUNDS)) promises.push(loadIceland().then(m => m.fetchIcelandicHeritage(bbox)));
        if (bboxOverlaps(bbox, FO_BOUNDS)) {
          promises.push(loadFaroe().then(m => m.fetchFaroeseHeritage(bbox)));
          promises.push(loadFaroe().then(m => m.fetchFaroeseWarHeritage(bbox)));
        }
        if (bboxOverlaps(bbox, GL_BOUNDS)) {
          promises.push(loadGreenland().then(m => m.fetchGreenlandicHeritage(bbox)));
          promises.push(loadGreenland().then(m => m.fetchGreenlandicNorse(bbox)));
          promises.push(loadGreenland().then(m => m.fetchGreenlandicThule(bbox)));
        }
        // England, Wales and Scotland have their own boxes since 18 Sep 2026.
        // GB_BOUNDS reaches to 8,2° W, which is most of Ireland, so a reader
        // in Dublin was asking four British registers about Dublin.
        if (bboxOverlapsAny(bbox, EN_BOUNDS)) {
          promises.push(loadEngland().then(m => m.fetchEnglishHeritage(bbox)));
          promises.push(loadWales().then(m => m.fetchWelshHeritage(bbox)));
          promises.push(loadWales().then(m => m.fetchRcahmwHeritage(bbox)));
        }
        if (bboxOverlapsAny(bbox, SC_BOUNDS)) promises.push(loadScotland().then(m => m.fetchScottishHeritage(bbox)));
        if (bboxOverlaps(bbox, NI_BOUNDS) && local) promises.push(loadNI().then(m => m.fetchNIHeritage(bbox)));
        if (bboxOverlaps(bbox, IE_BOUNDS)) {
          promises.push(loadIreland().then(m => m.fetchIrishHeritage(bbox)));
          promises.push(loadIreland().then(m => m.fetchIrishHolyWells(bbox)));
          promises.push(loadIreland().then(m => m.fetchIrishStoneCircles(bbox)));
          promises.push(loadIreland().then(m => m.fetchIrishSweathouses(bbox)));
          promises.push(loadIreland().then(m => m.fetchIrishMonumentsToVisit(bbox)));
        }
        // Accumulate new sites from national APIs
        if (promises.length) {
          const results = await Promise.allSettled(promises);
          results.forEach((r) => {
            if (r.status === "fulfilled") r.value.forEach(s => {
              const had = heritageAccumRef.current.get(s.id);
              // A Danish pin fetched at a wide view carries only its type as a
              // name (the name tiles need a view under 2°); the same record
              // fetched closer in has its name, and that one wins.
              if (!had || (had.name === had.type && s.name !== s.type)) heritageAccumRef.current.set(s.id, s);
            });
          });
          if (results.every((r) => r.status === "rejected")) setError(t("error.noData", langRef.current));
          else if (results.some((r) => r.status === "rejected")) setError(t("error.partialData", langRef.current));
          else setError(null);
        }
        // Always add static data (Wikidata, Doggerland, Roman, hillforts, hoards)
        addStaticHeritage();
        publishHeritage();
      } catch (e) {
        console.warn("Heritage fetch failed:", e);
        setError(t("error.noData", langRef.current));
      } finally {
        setLoading(false);
      }
    }, 400);
  }, []);

  // --- FOLKLORE FETCH ---
  const fetchFolkloreSites = useCallback((bbox: BBox) => {
    if (folkDebounceRef.current) clearTimeout(folkDebounceRef.current);
    folkDebounceRef.current = setTimeout(async () => {
      const last = folkLastBboxRef.current;
      if (last && Math.abs(bbox.minLng - last.minLng) < 0.01 && Math.abs(bbox.minLat - last.minLat) < 0.01 && Math.abs(bbox.maxLng - last.maxLng) < 0.01 && Math.abs(bbox.maxLat - last.maxLat) < 0.01) return;
      folkLastBboxRef.current = bbox;
      if (!bboxOverlaps(bbox, SCAND_BOUNDS)) return;
      // ISOF's 200 legends for a view of all Scandinavia carry their full
      // texts, 0,5 MB on a first visit (26 Sep 2026), and at that zoom they are
      // a thin sprinkle. Same rule as the live registers: on the default view
      // of everything they wait for a view that fits a country; a reader who
      // picks the legend layer gets them at any zoom. The last-bbox guard is
      // cleared so the next view does fetch.
      if (browsingEverythingRef.current && !viewportFitsACountryFile(bbox)) { folkLastBboxRef.current = null; return; }
      try {
        const isofRecords = await fetchISoFLegends(bbox);
        // Clear old ISOF entries from both indexes
        for (const [id] of folkloreIndex.current) {
          if (id.startsWith("isof-")) folkloreIndex.current.delete(id);
        }
        for (const [ck, arr] of folkloreByCoord.current) {
          const filtered = arr.filter(r => !r.id.startsWith("isof-"));
          if (filtered.length === 0) folkloreByCoord.current.delete(ck);
          else folkloreByCoord.current.set(ck, filtered);
        }
        isofRecords.forEach((r) => {
          folkloreIndex.current.set(r.id, r);
          const ck = `${r.lat},${r.lng}`;
          const arr = folkloreByCoord.current.get(ck) ?? [];
          if (!isFolkloreDuplicate(arr, r)) arr.push(r);
          folkloreByCoord.current.set(ck, arr);
        });
        const isofFeatures = isofRecords.map((r) => ({
          type: "Feature" as const,
          geometry: { type: "Point" as const, coordinates: [r.lng, r.lat] },
          properties: { id: r.id, name: r.name, layerType: "folklore", category: r.category, country: r.country },
        }));
        // Keep non-ISOF records (nexus2, saga, sagnagrunnur), replace ISOF records
        setFolkloreFeatures((prev) => {
          const kept = prev.filter((f) => !f.properties?.id?.startsWith("isof-"));
          return [...kept, ...isofFeatures];
        });
        const staticCount = (getDanishFolkloreCache()?.length ?? 0) + (getSagaCache()?.length ?? 0) + (getSagnagrunnurCache()?.length ?? 0) + (getFinnishFolkloreCache()?.length ?? 0);
        setFolkloreCounts(staticCount + isofRecords.length);
      } catch { /* silent fail */ }
    }, 400);
  }, []);

  // --- BUILDING FETCH ---
  const fetchBuildingSites = useCallback((bbox: BBox) => {
    if (bldDebounceRef.current) clearTimeout(bldDebounceRef.current);
    bldDebounceRef.current = setTimeout(async () => {
      const last = bldLastBboxRef.current;
      if (last && Math.abs(bbox.minLng - last.minLng) < 0.01 && Math.abs(bbox.minLat - last.minLat) < 0.01 && Math.abs(bbox.maxLng - last.maxLng) < 0.01 && Math.abs(bbox.maxLat - last.maxLat) < 0.01) return;
      bldLastBboxRef.current = bbox;
      addLoading("monument");
      // A country-sized file is worth fetching for a view of a place and not
      // for a view of a continent; utils/geo.ts::viewportFitsACountryFile has
      // the measurement. Live per-bbox fetchers below are not gated on it.
      const local = !browsingEverythingRef.current || viewportFitsACountryFile(bbox);

      try {
        const promises: Promise<ProtectedBuilding[]>[] = [];
        if (bboxOverlaps(bbox, DK_BOUNDS)) promises.push(loadBuildings().then(m => m.fetchDanishBuildings(bbox)));
        if (bboxOverlaps(bbox, NO_BOUNDS) && local) promises.push(loadBuildings().then(m => m.fetchNorwegianBuildings(bbox)));
        if (bboxOverlaps(bbox, SE_BOUNDS) && local) promises.push(loadBuildings().then(m => m.fetchSwedishBuildings(bbox)));
        if (bboxOverlaps(bbox, FI_BOUNDS)) promises.push(loadFinland().then(m => m.fetchFinnishBuildings(bbox)));
        if (bboxOverlaps(bbox, IS_BOUNDS)) promises.push(loadIceland().then(m => m.fetchIcelandicBuildings(bbox)));
        if (bboxOverlaps(bbox, FO_BOUNDS)) promises.push(loadFaroe().then(m => m.fetchFaroeseBuildings(bbox)));
        if (bboxOverlaps(bbox, GL_BOUNDS)) promises.push(loadGreenland().then(m => m.fetchGreenlandicBuildings(bbox)));
        if (bboxOverlapsAny(bbox, EN_BOUNDS)) {
          if (local) promises.push(loadEngland().then(m => m.fetchEnglishBuildings(bbox)));
          promises.push(loadWales().then(m => m.fetchWelshBuildings(bbox)));
        }
        // buildings-sc.json is 1,8 MB of Scotland and was arriving in Dublin.
        if (bboxOverlapsAny(bbox, SC_BOUNDS) && local) promises.push(loadScotland().then(m => m.fetchScottishBuildings(bbox)));
        if (bboxOverlaps(bbox, NI_BOUNDS) && local) promises.push(loadNI().then(m => m.fetchNIBuildings(bbox)));
        if (bboxOverlaps(bbox, IE_BOUNDS) && local) promises.push(loadIreland().then(m => m.fetchIrishBuildings(bbox)));
        if (bboxOverlaps(bbox, EE_BOUNDS)) promises.push(loadEstonia().then(m => m.fetchEstonianValuedBuildings(bbox)));
        if (bboxOverlaps(bbox, NL_BOUNDS)) promises.push(loadNetherlands().then(m => m.fetchDutchBuildings(bbox)));
        // One gate for the country; api/germany.ts gates again per Land on
        // the register's own box, so a Frankfurt viewport asks Hessen only.
        if (bboxOverlaps(bbox, DE_BOUNDS)) promises.push(loadGermany().then(m => m.fetchGermanBuildings(bbox)));
        if (promises.length) {
          const results = await Promise.allSettled(promises);
          results.forEach(r => { if (r.status === "fulfilled") r.value.forEach(b => { if (!buildingIndex.current.has(b.id)) buildingIndex.current.set(b.id, b); }); });
        }
        // England and Wales state when a building was listed, never when it
        // was built; this fills that in from Wikidata's list entry numbers
        // where it can, and leaves the rest undated (api/buildingYears.ts).
        const all = await withBuildingYears(Array.from(buildingIndex.current.values()));
        all.forEach(b => buildingIndex.current.set(b.id, b));
        setBuildingFeatures(toSimpleGeoJSON(all, "building").features);
        setBuildingCount(all.length);
      } catch (e) {
        console.warn("Building fetch failed:", e);
      } finally {
        removeLoading("monument");
      }
    }, 400);
  }, []);

  // --- SHIPWRECK FETCH ---
  // Accumulative: once a wreck is fetched it stays in the index, so ocean wrecks
  // from national APIs (e.g. Danish maritime wrecks in the North Sea) remain visible
  // even when the viewport no longer overlaps the country bounds.
  const fetchShipwreckSites = useCallback((bbox: BBox) => {
    if (wreckDebounceRef.current) clearTimeout(wreckDebounceRef.current);
    wreckDebounceRef.current = setTimeout(async () => {
      const last = wreckLastBboxRef.current;
      if (last && Math.abs(bbox.minLng - last.minLng) < 0.01 && Math.abs(bbox.minLat - last.minLat) < 0.01 && Math.abs(bbox.maxLng - last.maxLng) < 0.01 && Math.abs(bbox.maxLat - last.maxLat) < 0.01) return;
      wreckLastBboxRef.current = bbox;
      addLoading("shipwreck");
      // A country-sized file is worth fetching for a view of a place and not
      // for a view of a continent; utils/geo.ts::viewportFitsACountryFile has
      // the measurement. Live per-bbox fetchers below are not gated on it.
      const local = !browsingEverythingRef.current || viewportFitsACountryFile(bbox);

      try {
        const promises: Promise<Shipwreck[]>[] = [];
        if (bboxOverlaps(bbox, DK_BOUNDS)) promises.push(loadShipwrecks().then(m => m.fetchDanishShipwrecks(bbox)));
        if (bboxOverlaps(bbox, NO_BOUNDS) && local) promises.push(loadShipwrecks().then(m => m.fetchNorwegianShipwrecks(bbox)));
        if (bboxOverlaps(bbox, SE_BOUNDS) && local) promises.push(loadShipwrecks().then(m => m.fetchSwedishShipwrecks(bbox)));
        if (bboxOverlaps(bbox, FI_BOUNDS)) promises.push(loadFinland().then(m => m.fetchFinnishShipwrecks(bbox)));
        if (bboxOverlaps(bbox, GB_BOUNDS)) {
          promises.push(loadShipwrecks().then(m => m.fetchEnglishShipwrecks(bbox)));
          if (local) promises.push(loadNI().then(m => m.fetchNIShipwrecks(bbox)));
          promises.push(loadWales().then(m => m.fetchWelshShipwrecks(bbox)));
          if (local) promises.push(loadShipwrecks().then(m => m.loadGBShipwrecksFromStatic()));
        }
        if (bboxOverlaps(bbox, IE_WRECK_BOUNDS) && local) promises.push(loadShipwrecks().then(m => m.fetchIrishShipwrecks(bbox)));
        if (bboxOverlaps(bbox, IS_BOUNDS) || bboxOverlaps(bbox, GL_BOUNDS)) promises.push(loadShipwrecks().then(m => m.fetchOverpassShipwrecks(bbox)));
        // Fetch new wrecks from national APIs (if any overlap)
        if (promises.length) {
          const results = await Promise.allSettled(promises);
          results.forEach(r => { if (r.status === "fulfilled") r.value.forEach(s => { if (!shipwreckIndex.current.has(s.id)) shipwreckIndex.current.set(s.id, s); }); });
        }
        // Add Wikidata wrecks not already in index
        for (const wd of wdWreckCacheRef.current) {
          if (!shipwreckIndex.current.has(wd.id)) shipwreckIndex.current.set(wd.id, wd);
        }
        // Build final array from accumulated index, filtering heritage overlaps + inland wrecks
        const all = Array.from(shipwreckIndex.current.values())
          .filter(s => !heritageCoordSet.has(`${s.lng.toFixed(3)},${s.lat.toFixed(3)}`))
          .filter(s => !isLikelyInland(s.lat, s.lng));
        setShipwreckFeatures(toSimpleGeoJSON(all, "shipwreck").features);
        setShipwreckCount(all.length);
      } catch (e) {
        console.warn("Shipwreck fetch failed:", e);
      } finally {
        removeLoading("shipwreck");
      }
    }, 400);
  }, [heritageCoordSet]);

  // --- ROCK ART FETCH ---
  const fetchRockArtSites = useCallback((bbox: BBox) => {
    if (rockDebounceRef.current) clearTimeout(rockDebounceRef.current);
    rockDebounceRef.current = setTimeout(async () => {
      const last = rockLastBboxRef.current;
      if (last && Math.abs(bbox.minLng - last.minLng) < 0.01 && Math.abs(bbox.minLat - last.minLat) < 0.01 && Math.abs(bbox.maxLng - last.maxLng) < 0.01 && Math.abs(bbox.maxLat - last.maxLat) < 0.01) return;
      rockLastBboxRef.current = bbox;
      addLoading("rockart");
      // A country-sized file is worth fetching for a view of a place and not
      // for a view of a continent; utils/geo.ts::viewportFitsACountryFile has
      // the measurement. Live per-bbox fetchers below are not gated on it.
      const local = !browsingEverythingRef.current || viewportFitsACountryFile(bbox);

      try {
        const promises: Promise<RockArt[]>[] = [];
        if (bboxOverlaps(bbox, DK_BOUNDS)) promises.push(loadRockart().then(m => m.fetchDanishRockArt(bbox)));
        if (bboxOverlaps(bbox, NO_BOUNDS) && local) promises.push(loadRockart().then(m => m.fetchNorwegianRockArt(bbox)));
        if (bboxOverlaps(bbox, SE_BOUNDS) && local) promises.push(loadRockart().then(m => m.fetchSwedishRockArt(bbox)));
        if (bboxOverlaps(bbox, FI_BOUNDS)) promises.push(loadFinland().then(m => m.fetchFinnishRockArt(bbox)));
        if (bboxOverlapsAny(bbox, EN_BOUNDS) && local) promises.push(loadRockart().then(m => m.fetchEnglishRockArt(bbox)));
        if (bboxOverlaps(bbox, IE_BOUNDS) && local) promises.push(loadRockart().then(m => m.fetchIrishRockArt(bbox)));
        if (promises.length) {
          const results = await Promise.allSettled(promises);
          results.forEach(r => { if (r.status === "fulfilled") r.value.forEach(s => { if (!rockArtIndex.current.has(s.id)) rockArtIndex.current.set(s.id, s); }); });
        }
        const all = Array.from(rockArtIndex.current.values())
          .filter(s => !heritageCoordSet.has(`${s.lng.toFixed(3)},${s.lat.toFixed(3)}`));
        setRockArtFeatures(toSimpleGeoJSON(all, "rockart").features);
        setRockArtCount(all.length);
      } catch (e) {
        console.warn("Rock art fetch failed:", e);
      } finally {
        removeLoading("rockart");
      }
    }, 400);
  }, [heritageCoordSet]);

  // --- INDUSTRIAL FETCH ---
  const fetchIndustrialSites = useCallback((bbox: BBox) => {
    if (indDebounceRef.current) clearTimeout(indDebounceRef.current);
    indDebounceRef.current = setTimeout(async () => {
      const last = indLastBboxRef.current;
      if (last && Math.abs(bbox.minLng - last.minLng) < 0.01 && Math.abs(bbox.minLat - last.minLat) < 0.01 && Math.abs(bbox.maxLng - last.maxLng) < 0.01 && Math.abs(bbox.maxLat - last.maxLat) < 0.01) return;
      indLastBboxRef.current = bbox;
      addLoading("industrial");
      // A country-sized file is worth fetching for a view of a place and not
      // for a view of a continent; utils/geo.ts::viewportFitsACountryFile has
      // the measurement. Live per-bbox fetchers below are not gated on it.
      const local = !browsingEverythingRef.current || viewportFitsACountryFile(bbox);

      try {
        const promises: Promise<IndustrialSite[]>[] = [];
        if (bboxOverlaps(bbox, DK_BOUNDS)) {
          promises.push(loadIndustrial().then(m => m.fetchDanishIndustrial(bbox)));
          if (local) promises.push(loadIndustrial().then(m => m.fetchSlksIndustrial(bbox)));
        }
        if (bboxOverlaps(bbox, NO_BOUNDS) && local) promises.push(loadIndustrial().then(m => m.fetchNorwegianIndustrial(bbox)));
        if (bboxOverlaps(bbox, SE_BOUNDS) && local) promises.push(loadIndustrial().then(m => m.fetchSwedishIndustrial(bbox)));
        if (bboxOverlaps(bbox, FI_BOUNDS)) promises.push(loadFinland().then(m => m.fetchFinnishIndustrial(bbox)));
        if (bboxOverlaps(bbox, IS_BOUNDS)) promises.push(loadIceland().then(m => m.fetchIcelandicIndustrial(bbox)));
        if (bboxOverlaps(bbox, GL_BOUNDS)) promises.push(loadGreenland().then(m => m.fetchGreenlandicIndustrial(bbox)));
        if (bboxOverlaps(bbox, GB_BOUNDS)) {
          if (local) promises.push(loadIndustrial().then(m => m.fetchUkCuratedIndustrial(bbox)));
          if (local && bboxOverlapsAny(bbox, EN_BOUNDS)) promises.push(loadIndustrial().then(m => m.fetchEnglishIndustrial(bbox)));
        }
        if (bboxOverlaps(bbox, NI_BOUNDS) && local) promises.push(loadNI().then(m => m.fetchNIIndustrial(bbox)));
        if (bboxOverlaps(bbox, IE_BOUNDS) && local) promises.push(loadIndustrial().then(m => m.fetchUkCuratedIndustrial(bbox)));
        if (promises.length) {
          const results = await Promise.allSettled(promises);
          const raw: IndustrialSite[] = [];
          results.forEach(r => { if (r.status === "fulfilled") raw.push(...r.value); });
          // Deduplicate: prefer SLKS entries (richer descriptions) over WFS entries at same location
          raw.sort((a, b) => (a.id.startsWith("slks-") ? -1 : 1) - (b.id.startsWith("slks-") ? -1 : 1));
          for (const s of raw) {
            if (!industrialIndex.current.has(s.id)) industrialIndex.current.set(s.id, s);
          }
        }
        const all = Array.from(industrialIndex.current.values());
        setIndustrialFeatures(toSimpleGeoJSON(all, "industrial").features);
        setIndustrialCount(all.length);
      } catch (e) {
        console.warn("Industrial fetch failed:", e);
      } finally {
        removeLoading("industrial");
      }
    }, 400);
  }, []);

  // --- BARROW FETCH ---
  const fetchBarrowSites = useCallback((bbox: BBox) => {
    if (barDebounceRef.current) clearTimeout(barDebounceRef.current);
    barDebounceRef.current = setTimeout(async () => {
      const last = barLastBboxRef.current;
      if (last && Math.abs(bbox.minLng - last.minLng) < 0.01 && Math.abs(bbox.minLat - last.minLat) < 0.01 && Math.abs(bbox.maxLng - last.maxLng) < 0.01 && Math.abs(bbox.maxLat - last.maxLat) < 0.01) return;
      barLastBboxRef.current = bbox;
      addLoading("barrow");
      // A country-sized file is worth fetching for a view of a place and not
      // for a view of a continent; utils/geo.ts::viewportFitsACountryFile has
      // the measurement. Live per-bbox fetchers below are not gated on it.
      const local = !browsingEverythingRef.current || viewportFitsACountryFile(bbox);

      try {
        const promises: Promise<BarrowSite[]>[] = [];
        if (bboxOverlaps(bbox, DK_BOUNDS)) promises.push(loadBarrow().then(m => m.fetchDanishBarrows(bbox)));
        if (bboxOverlaps(bbox, NO_BOUNDS) && local) promises.push(loadBarrow().then(m => m.fetchNorwegianBarrows(bbox)));
        if (bboxOverlaps(bbox, SE_BOUNDS) && local) promises.push(loadBarrow().then(m => m.fetchSwedishBarrows(bbox)));
        if (bboxOverlaps(bbox, FI_BOUNDS)) promises.push(loadFinland().then(m => m.fetchFinnishBarrows(bbox)));
        if (bboxOverlaps(bbox, IS_BOUNDS)) promises.push(loadIceland().then(m => m.fetchIcelandicBarrows(bbox)));
        // barrows-en.json is 219 kB on the wire and stops at the Welsh coast.
        if (bboxOverlapsAny(bbox, EN_BOUNDS) && local) promises.push(loadBarrow().then(m => m.fetchEnglishBarrows(bbox)));
        if (bboxOverlaps(bbox, IE_BOUNDS)) promises.push(loadBarrow().then(m => m.fetchIrishBarrows(bbox)));
        if (promises.length) {
          const results = await Promise.allSettled(promises);
          results.forEach(r => { if (r.status === "fulfilled") r.value.forEach(s => { if (!barrowIndex.current.has(s.id)) barrowIndex.current.set(s.id, s); }); });
        }
        const all = Array.from(barrowIndex.current.values())
          .filter(s => !heritageCoordSet.has(`${s.lng.toFixed(3)},${s.lat.toFixed(3)}`));
        setBarrowFeatures(toSimpleGeoJSON(all, "barrow").features);
        setBarrowCount(all.length);
      } catch (e) {
        console.warn("Barrow fetch failed:", e);
      } finally {
        removeLoading("barrow");
      }
    }, 400);
  }, [heritageCoordSet]);

  // --- ENGLISH TOWNSCAPE FETCH ---
  const fetchEnglishTownscapeSites = useCallback((bbox: BBox) => {
    if (enTownDebounceRef.current) clearTimeout(enTownDebounceRef.current);
    enTownDebounceRef.current = setTimeout(async () => {
      // townscapes-en.json is one file for the whole of England; a view of
      // northern Europe overlaps it and is not worth it (utils/geo.ts).
      if (!bboxOverlapsAny(bbox, EN_BOUNDS) || !viewportFitsACountryFile(bbox)) return;
      const last = enTownLastBboxRef.current;
      if (last && Math.abs(bbox.minLng - last.minLng) < 0.01 && Math.abs(bbox.minLat - last.minLat) < 0.01 && Math.abs(bbox.maxLng - last.maxLng) < 0.01 && Math.abs(bbox.maxLat - last.maxLat) < 0.01) return;
      enTownLastBboxRef.current = bbox;
      try {
        const items = await loadTownscape().then(m => m.fetchEnglishTownscapes(bbox));
        if (!items.length) return;
        // Merge into existing townscape index (avoid duplicates)
        let added = 0;
        for (const s of items) {
          if (!townscapeIndex.current.has(s.id)) { townscapeIndex.current.set(s.id, s); added++; }
        }
        if (added > 0) {
          const all = Array.from(townscapeIndex.current.values());
          const features = toSimpleGeoJSON(all, "townscape").features;
          const curatedIds = new Set(all.filter(s => !!s.values).map(s => s.id));
          for (const f of features) {
            if (curatedIds.has(f.properties?.id) && f.properties) {
              (f.properties as { curated?: boolean }).curated = true;
            }
          }
          setTownscapeFeatures(features);
          setTownscapeCount(all.length);
        }
      } catch (e) { console.warn("English townscapes fetch failed:", e); }
    }, 400);
  }, []);

  // Helper: fetch all dynamic data for a set of visible layer types
  const fetchForLayers = useCallback((layers: Set<ActiveTab>, bbox: BBox) => {
    if (layers.has("heritage")) fetchHeritageSites(bbox);
    if (layers.has("folklore")) fetchFolkloreSites(bbox);
    if (layers.has("monument")) fetchBuildingSites(bbox);
    if (layers.has("shipwreck")) fetchShipwreckSites(bbox);
    if (layers.has("rockart")) fetchRockArtSites(bbox);
    if (layers.has("industrial")) fetchIndustrialSites(bbox);
    if (layers.has("barrow")) fetchBarrowSites(bbox);
    if (layers.has("townscape")) fetchEnglishTownscapeSites(bbox);
  }, [fetchHeritageSites, fetchFolkloreSites, fetchBuildingSites, fetchShipwreckSites, fetchRockArtSites, fetchIndustrialSites, fetchBarrowSites, fetchEnglishTownscapeSites]);

  const getVisibleLayers = useCallback((group: CategoryGroup, tab: ActiveTab): Set<ActiveTab> => {
    if (group === "all" && tab === "all") return new Set(["heritage", "folklore", "monument", "shipwreck", "rockart", "industrial", "barrow", "townscape"] as ActiveTab[]);
    if (tab === "all") return new Set(GROUP_TABS[group] as ActiveTab[]);
    return new Set([tab]);
  }, []);

  return { fetchForLayers, getVisibleLayers, fetchHeritageSites, mergeStaticHeritage };
}
